import { useEffect, useMemo, useState } from "react";
import { FiPlus, FiTrash2, FiX } from "react-icons/fi";
import API from "../api/axios";
import TablePagination from "../components/TablePagination";

const FLOW_STEPS = [
  { key: "applied", label: "Apply", detail: "Customer submits details for 2 members" },
  { key: "under_review", label: "Review", detail: "Yatra369 reviews eligibility" },
  { key: "payment_pending", label: "Payment", detail: "Pay ₹60,000 membership fee" },
  { key: "confirmed", label: "Confirmation", detail: "Membership ID is issued" },
  { key: "active", label: "Activation", detail: "Membership becomes active" },
  { key: "selected", label: "Tour Selection", detail: "Choose an eligible tour" },
  { key: "booked", label: "Tour Booking", detail: "Confirm dates and booking" },
  { key: "travelling", label: "Travel", detail: "Member and second member travel" },
  { key: "completed", label: "Utilisation", detail: "Completed tour counted in 10" },
  { key: "repeat", label: "Repeat & Explore", detail: "Use remaining tours in 5 years" },
];

const STATUS_LABELS = {
  applied: "Applied",
  under_review: "Under Review",
  rejected: "Rejected",
  payment_pending: "Payment Pending",
  paid: "Paid",
  confirmed: "Confirmed",
  active: "Active",
  expired: "Expired",
  cancelled: "Cancelled",
};

const emptyMember = {
  fullName: "",
  phone: "",
  email: "",
  relationship: "",
  dateOfBirth: "",
  address: "",
  city: "",
  idType: "",
  idNumber: "",
};

const emptyForm = {
  primaryMember: { ...emptyMember, relationship: "Self" },
  secondMember: { ...emptyMember, relationship: "Spouse" },
  notes: "",
  feeAmount: 60000,
};

const currentStepIndex = (item) => {
  if (!item) return 0;
  if (item.status === "rejected") return 1;
  if (item.status === "applied") return 0;
  if (item.status === "under_review") return 1;
  if (item.status === "payment_pending") return 2;
  if (item.status === "paid") return 2;
  if (item.status === "confirmed") return 3;
  if (item.status === "active" || item.status === "expired") {
    if (item.toursUsed > 0) return 9;
    const latest = [...(item.tours || [])].reverse().find((tour) => tour.status !== "cancelled");
    if (!latest) return 5;
    if (latest.status === "selected") return 5;
    if (latest.status === "booked") return 6;
    if (latest.status === "travelling") return 7;
    if (latest.status === "completed") return 8;
    return 5;
  }
  return 0;
};

const formatDate = (value) => {
  if (!value) return "-";
  return new Date(value).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

export default function Memberships() {
  const [items, setItems] = useState([]);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [selected, setSelected] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [payment, setPayment] = useState({ paymentMethod: "upi", paymentReference: "", feeAmount: 60000 });
  const [reviewNotes, setReviewNotes] = useState("");
  const [rejectionReason, setRejectionReason] = useState("");
  const [tourForm, setTourForm] = useState({
    packageId: "",
    packageName: "",
    destination: "",
    preferredDates: "",
    requirements: "",
  });

  const filtered = useMemo(() => {
    return items.filter((item) => {
      if (statusFilter !== "all" && item.status !== statusFilter) return false;
      if (!query.trim()) return true;
      const haystack = [
        item.membershipId,
        item.primaryMember?.fullName,
        item.primaryMember?.phone,
        item.secondMember?.fullName,
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(query.trim().toLowerCase());
    });
  }, [items, statusFilter, query]);

  const paged = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page, pageSize]);

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [membershipRes, packageRes] = await Promise.all([
        API.get("/admin/memberships"),
        API.get("/admin/packages", { params: { type: "tour" } }),
      ]);
      setItems(membershipRes.data?.data || []);
      setPackages(packageRes.data?.data || []);
    } catch (err) {
      setError(err.response?.data?.message || "Unable to load memberships.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  useEffect(() => {
    setPage(1);
  }, [statusFilter, query, filtered.length]);

  const updateMember = (key, field, value) => {
    setForm((current) => ({
      ...current,
      [key]: { ...current[key], [field]: value },
    }));
  };

  const handleCreate = async (event) => {
    event.preventDefault();
    if (!form.primaryMember.fullName || !form.primaryMember.phone || !form.secondMember.fullName) {
      setError("Primary member name/phone and second member name are required.");
      return;
    }

    try {
      setSubmitting(true);
      setError("");
      const res = await API.post("/admin/memberships", form);
      await fetchAll();
      setSelected(res.data?.data || null);
      setShowCreate(false);
      setForm(emptyForm);
      setSuccess("Membership application created.");
    } catch (err) {
      setError(err.response?.data?.message || "Unable to create application.");
    } finally {
      setSubmitting(false);
    }
  };

  const runAction = async (request, successMessage) => {
    try {
      setSubmitting(true);
      setError("");
      const res = await request();
      const next = res.data?.data;
      setItems((current) => current.map((item) => (item._id === next._id ? next : item)));
      setSelected(next);
      setSuccess(successMessage);
    } catch (err) {
      setError(err.response?.data?.message || "Unable to update membership.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete application for ${item.primaryMember?.fullName}?`)) return;
    try {
      await API.delete(`/admin/memberships/${item._id}`);
      const next = items.filter((entry) => entry._id !== item._id);
      setItems(next);
      if (selected?._id === item._id) setSelected(null);
      setSuccess("Membership deleted.");
    } catch (err) {
      setError(err.response?.data?.message || "Unable to delete membership.");
    }
  };

  const stepIndex = currentStepIndex(selected);
  const remainingTours = Math.max(0, Number(selected?.toursEntitled || 10) - Number(selected?.toursUsed || 0));

  const inputClass = "w-full rounded-xl border border-[var(--admin-border)] bg-[var(--admin-input)] px-3 py-2 text-sm";

  return (
    <div className="space-y-6 text-[#2f1618] dark:text-[#fff3dc]">
      <section className="rounded-3xl border border-[var(--admin-border)] bg-[var(--admin-surface)] p-6 shadow-[var(--admin-shadow)]">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-2xl font-bold">Memberships</h2>
            <p className="mt-1 text-sm text-[#7b5a4b] dark:text-[#dbcdb8]/70">
              10-step Yatra369 membership: apply, review, ₹60,000 payment, activation, then 10 tours in 5 years.
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              setShowCreate(true);
              setSelected(null);
              setError("");
              setSuccess("");
            }}
            className="admin-btn-primary flex items-center gap-2 rounded-2xl border border-transparent px-4 py-2 text-sm font-medium"
          >
            <FiPlus size={18} />
            Add Application
          </button>
        </div>

        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <select
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
            className="h-10 rounded-xl border border-[#d7c3a3] bg-white px-3 text-sm dark:bg-[#181c24] dark:text-white dark:border-white/20"
          >
            <option value="all">All statuses</option>
            {Object.entries(STATUS_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search name, phone or membership ID"
            className="w-full max-w-xs rounded-xl border border-[var(--admin-border)] bg-[var(--admin-input)] px-4 py-2 text-sm"
          />
        </div>

        {error && <div className="mb-4 rounded-xl bg-red-100 p-4 text-red-800">{error}</div>}
        {success && <div className="mb-4 rounded-xl bg-green-100 p-4 text-green-800">{success}</div>}

        {loading ? (
          <div className="py-8 text-center">Loading memberships...</div>
        ) : filtered.length === 0 ? (
          <div className="py-8 text-center">No membership applications yet.</div>
        ) : (
          <>
            <div className="admin-table-wrap overflow-x-auto">
              <table className="admin-table w-full text-sm">
                <thead>
                  <tr className="border-b border-[#e6d8c5] text-xs uppercase tracking-[0.18em] text-[#7f5a4f] dark:border-white/10 dark:text-[#e7c98b]">
                    <th className="px-3 py-3 text-left">S.No</th>
                    <th className="px-3 py-3 text-left">Members</th>
                    <th className="px-3 py-3 text-left">Membership ID</th>
                    <th className="px-3 py-3 text-left">Status</th>
                    <th className="px-3 py-3 text-left">Tours</th>
                    <th className="px-3 py-3 text-left">Applied</th>
                    <th className="px-3 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paged.map((item, index) => (
                    <tr
                      key={item._id}
                      className={`border-b border-[var(--admin-border)] hover:bg-[var(--admin-surface-soft)] ${
                        selected?._id === item._id ? "bg-[var(--admin-surface-soft)]" : ""
                      }`}
                    >
                      <td className="px-3 py-3">{(page - 1) * pageSize + index + 1}</td>
                      <td className="px-3 py-3">
                        <p className="font-semibold text-[#D4AF37]">{item.primaryMember?.fullName}</p>
                        <p className="text-xs text-[#7b5a4b]">+ {item.secondMember?.fullName}</p>
                        <p className="text-xs">{item.primaryMember?.phone}</p>
                      </td>
                      <td className="px-3 py-3 font-mono text-xs">{item.membershipId || "-"}</td>
                      <td className="px-3 py-3">
                        <span className="inline-block rounded-full bg-[#102C57]/10 px-3 py-1 text-xs font-medium">
                          {STATUS_LABELS[item.status] || item.status}
                        </span>
                      </td>
                      <td className="px-3 py-3">
                        {item.toursUsed || 0}/{item.toursEntitled || 10}
                      </td>
                      <td className="px-3 py-3">{formatDate(item.createdAt)}</td>
                      <td className="px-3 py-3 text-right">
                        <button
                          type="button"
                          onClick={() => {
                            setSelected(item);
                            setShowCreate(false);
                            setReviewNotes(item.reviewNotes || "");
                            setPayment({
                              paymentMethod: item.paymentMethod || "upi",
                              paymentReference: item.paymentReference || "",
                              feeAmount: item.feeAmount || 60000,
                            });
                          }}
                          className="mr-2 rounded-xl border border-[var(--admin-border)] px-3 py-1 text-xs font-semibold"
                        >
                          Open Flow
                        </button>
                        <button type="button" onClick={() => handleDelete(item)} className="text-red-600">
                          <FiTrash2 />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <TablePagination
              page={page}
              pageSize={pageSize}
              total={filtered.length}
              onPageChange={setPage}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPage(1);
              }}
              pageSizeOptions={[10]}
            />
          </>
        )}
      </section>

      {showCreate && (
        <section className="rounded-3xl border border-[var(--admin-border)] bg-[var(--admin-surface)] p-6 shadow-[var(--admin-shadow)]">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-xl font-bold">Step 01 — Membership Application</h3>
            <button type="button" onClick={() => setShowCreate(false)}>
              <FiX size={22} />
            </button>
          </div>
          <form onSubmit={handleCreate} className="space-y-6">
            {[
              { key: "primaryMember", title: "Member 1 (Primary)" },
              { key: "secondMember", title: "Member 2" },
            ].map((block) => (
              <div key={block.key} className="rounded-2xl border border-[var(--admin-border)] p-4">
                <h4 className="mb-3 font-semibold">{block.title}</h4>
                <div className="grid gap-3 md:grid-cols-2">
                  {[
                    ["fullName", "Full Name"],
                    ["phone", "Phone"],
                    ["email", "Email"],
                    ["relationship", "Relationship"],
                    ["dateOfBirth", "Date of Birth"],
                    ["city", "City"],
                    ["idType", "ID Type"],
                    ["idNumber", "ID Number"],
                  ].map(([field, label]) => (
                    <label key={field} className="text-sm">
                      <span className="mb-1 block font-medium">{label}</span>
                      <input
                        className={inputClass}
                        value={form[block.key][field]}
                        onChange={(event) => updateMember(block.key, field, event.target.value)}
                      />
                    </label>
                  ))}
                  <label className="text-sm md:col-span-2">
                    <span className="mb-1 block font-medium">Address</span>
                    <input
                      className={inputClass}
                      value={form[block.key].address}
                      onChange={(event) => updateMember(block.key, "address", event.target.value)}
                    />
                  </label>
                </div>
              </div>
            ))}
            <label className="block text-sm">
              <span className="mb-1 block font-medium">Notes</span>
              <textarea className={inputClass} rows="3" value={form.notes} onChange={(event) => setForm((current) => ({ ...current, notes: event.target.value }))} />
            </label>
            <button type="submit" disabled={submitting} className="admin-btn-primary rounded-2xl px-5 py-2 text-sm font-medium disabled:opacity-50">
              {submitting ? "Saving..." : "Submit Application"}
            </button>
          </form>
        </section>
      )}

      {selected && (
        <section className="rounded-3xl border border-[var(--admin-border)] bg-[var(--admin-surface)] p-6 shadow-[var(--admin-shadow)]">
          <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
            <div>
              <h3 className="text-xl font-bold">{selected.primaryMember?.fullName} & {selected.secondMember?.fullName}</h3>
              <p className="text-sm text-[#7b5a4b]">
                {selected.membershipId || "Membership ID pending"} · {STATUS_LABELS[selected.status]}
              </p>
            </div>
            <button type="button" onClick={() => setSelected(null)}>
              <FiX size={22} />
            </button>
          </div>

          <div className="mb-6 grid gap-2 md:grid-cols-5">
            {FLOW_STEPS.map((step, index) => (
              <div
                key={step.key}
                className={`rounded-2xl border px-3 py-3 text-xs ${
                  index <= stepIndex
                    ? "border-[#102C57] bg-[#102C57] text-white"
                    : "border-[var(--admin-border)] text-[#7b5a4b]"
                }`}
              >
                <p className="font-semibold">STEP {String(index + 1).padStart(2, "0")}</p>
                <p className="mt-1 font-bold">{step.label}</p>
                <p className="mt-1 opacity-80">{step.detail}</p>
              </div>
            ))}
          </div>

          <div className="mb-5 grid gap-4 md:grid-cols-3">
            <div className="rounded-2xl border border-[var(--admin-border)] p-4 text-sm">
              <p className="font-semibold">Entitlement</p>
              <p>₹{(selected.feeAmount || 60000).toLocaleString("en-IN")} / couple</p>
              <p>{selected.toursUsed || 0} of {selected.toursEntitled || 10} tours used</p>
              <p>{remainingTours} remaining · {selected.validityYears || 5} years</p>
              <p>Valid till {formatDate(selected.expiresAt)}</p>
            </div>
            <div className="rounded-2xl border border-[var(--admin-border)] p-4 text-sm">
              <p className="font-semibold">Member 1</p>
              <p>{selected.primaryMember?.fullName}</p>
              <p>{selected.primaryMember?.phone}</p>
              <p>{selected.primaryMember?.email || "-"}</p>
            </div>
            <div className="rounded-2xl border border-[var(--admin-border)] p-4 text-sm">
              <p className="font-semibold">Member 2</p>
              <p>{selected.secondMember?.fullName}</p>
              <p>{selected.secondMember?.phone || "-"}</p>
              <p>{selected.secondMember?.relationship || "-"}</p>
            </div>
          </div>

          <div className="space-y-4">
            {["applied", "under_review"].includes(selected.status) && (
              <div className="rounded-2xl border border-[var(--admin-border)] p-4">
                <h4 className="mb-3 font-semibold">Step 02 — Application Review</h4>
                <textarea className={`${inputClass} mb-3`} rows="2" placeholder="Review notes" value={reviewNotes} onChange={(event) => setReviewNotes(event.target.value)} />
                <div className="flex flex-wrap gap-2">
                  {selected.status === "applied" && (
                    <button type="button" disabled={submitting} onClick={() => runAction(() => API.post(`/admin/memberships/${selected._id}/review`, { action: "start", reviewNotes }), "Review started.")} className="admin-btn-primary rounded-xl px-4 py-2 text-sm">
                      Start Review
                    </button>
                  )}
                  <button type="button" disabled={submitting} onClick={() => runAction(() => API.post(`/admin/memberships/${selected._id}/review`, { action: "approve", reviewNotes }), "Applicant is eligible. Payment pending.")} className="rounded-xl border border-green-300 bg-green-50 px-4 py-2 text-sm font-semibold text-green-800">
                    Mark Eligible
                  </button>
                  <input className="rounded-xl border border-[var(--admin-border)] px-3 py-2 text-sm" placeholder="Rejection reason" value={rejectionReason} onChange={(event) => setRejectionReason(event.target.value)} />
                  <button type="button" disabled={submitting} onClick={() => runAction(() => API.post(`/admin/memberships/${selected._id}/review`, { action: "reject", reviewNotes, rejectionReason }), "Application rejected.")} className="rounded-xl border border-red-300 bg-red-50 px-4 py-2 text-sm font-semibold text-red-700">
                    Reject
                  </button>
                </div>
              </div>
            )}

            {["payment_pending", "paid"].includes(selected.status) && (
              <div className="rounded-2xl border border-[var(--admin-border)] p-4">
                <h4 className="mb-3 font-semibold">Step 03 — Membership Payment</h4>
                <div className="mb-3 grid gap-3 md:grid-cols-3">
                  <input className={inputClass} value={payment.feeAmount} onChange={(event) => setPayment((current) => ({ ...current, feeAmount: event.target.value }))} placeholder="Amount" />
                  <select className={inputClass} value={payment.paymentMethod} onChange={(event) => setPayment((current) => ({ ...current, paymentMethod: event.target.value }))}>
                    <option value="upi">UPI</option>
                    <option value="bank">Bank Transfer</option>
                    <option value="cash">Cash</option>
                    <option value="card">Card</option>
                  </select>
                  <input className={inputClass} value={payment.paymentReference} onChange={(event) => setPayment((current) => ({ ...current, paymentReference: event.target.value }))} placeholder="Payment reference" />
                </div>
                <div className="flex flex-wrap gap-2">
                  {selected.status === "payment_pending" && (
                    <button type="button" disabled={submitting} onClick={() => runAction(() => API.post(`/admin/memberships/${selected._id}/payment`, payment), "Payment recorded.")} className="admin-btn-primary rounded-xl px-4 py-2 text-sm">
                      Record ₹60,000 Payment
                    </button>
                  )}
                  {selected.status === "paid" && (
                    <button type="button" disabled={submitting} onClick={() => runAction(() => API.post(`/admin/memberships/${selected._id}/confirm`), "Membership ID issued.")} className="admin-btn-primary rounded-xl px-4 py-2 text-sm">
                      Step 04 — Issue Membership ID
                    </button>
                  )}
                </div>
              </div>
            )}

            {selected.status === "confirmed" && (
              <div className="rounded-2xl border border-[var(--admin-border)] p-4">
                <h4 className="mb-3 font-semibold">Step 05 — Membership Activation</h4>
                <p className="mb-3 text-sm">ID {selected.membershipId} is ready. Activate to start the 5-year / 10-tour entitlement.</p>
                <button type="button" disabled={submitting} onClick={() => runAction(() => API.post(`/admin/memberships/${selected._id}/activate`), "Membership activated.")} className="admin-btn-primary rounded-xl px-4 py-2 text-sm">
                  Activate Membership
                </button>
              </div>
            )}

            {selected.status === "active" && (
              <div className="rounded-2xl border border-[var(--admin-border)] p-4">
                <h4 className="mb-3 font-semibold">Steps 06 to 10 — Tour Selection, Booking, Travel, Utilisation</h4>
                <div className="mb-4 grid gap-3 md:grid-cols-2">
                  <select
                    className={inputClass}
                    value={tourForm.packageId}
                    onChange={(event) => {
                      const pkg = packages.find((entry) => entry._id === event.target.value);
                      setTourForm((current) => ({
                        ...current,
                        packageId: event.target.value,
                        packageName: pkg?.name || "",
                        destination: pkg?.destination || "",
                      }));
                    }}
                  >
                    <option value="">Select tour package</option>
                    {packages.map((pkg) => (
                      <option key={pkg._id} value={pkg._id}>
                        {pkg.name} {pkg.destination ? `· ${pkg.destination}` : ""}
                      </option>
                    ))}
                  </select>
                  <input className={inputClass} placeholder="Or type package name" value={tourForm.packageName} onChange={(event) => setTourForm((current) => ({ ...current, packageName: event.target.value }))} />
                  <input className={inputClass} placeholder="Preferred travel dates" value={tourForm.preferredDates} onChange={(event) => setTourForm((current) => ({ ...current, preferredDates: event.target.value }))} />
                  <input className={inputClass} placeholder="Tour requirements" value={tourForm.requirements} onChange={(event) => setTourForm((current) => ({ ...current, requirements: event.target.value }))} />
                </div>
                <button
                  type="button"
                  disabled={submitting || remainingTours <= 0}
                  onClick={() =>
                    runAction(async () => {
                      const res = await API.post(`/admin/memberships/${selected._id}/tours`, tourForm);
                      setTourForm({ packageId: "", packageName: "", destination: "", preferredDates: "", requirements: "" });
                      return res;
                    }, "Tour selected.")
                  }
                  className="admin-btn-primary mb-4 rounded-xl px-4 py-2 text-sm disabled:opacity-50"
                >
                  Step 06 — Select Tour
                </button>

                <div className="space-y-3">
                  {(selected.tours || []).length === 0 ? (
                    <p className="text-sm text-[#7b5a4b]">No tours selected yet. {remainingTours} of 10 remaining.</p>
                  ) : (
                    selected.tours.map((tour) => (
                      <div key={tour._id} className="rounded-xl border border-[var(--admin-border)] p-3 text-sm">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div>
                            <p className="font-semibold">{tour.packageName}</p>
                            <p className="text-xs text-[#7b5a4b]">
                              {tour.destination || "-"} · {tour.preferredDates || "Dates pending"} · {tour.status}
                            </p>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {tour.status === "selected" && (
                              <button type="button" onClick={() => runAction(() => API.patch(`/admin/memberships/${selected._id}/tours/${tour._id}`, { action: "book" }), "Tour booked.")} className="rounded-lg border px-3 py-1 text-xs">
                                Step 07 — Confirm Booking
                              </button>
                            )}
                            {tour.status === "booked" && (
                              <button type="button" onClick={() => runAction(() => API.patch(`/admin/memberships/${selected._id}/tours/${tour._id}`, { action: "travel" }), "Travel started.")} className="rounded-lg border px-3 py-1 text-xs">
                                Step 08 — Mark Travel
                              </button>
                            )}
                            {tour.status === "travelling" && (
                              <button type="button" onClick={() => runAction(() => API.patch(`/admin/memberships/${selected._id}/tours/${tour._id}`, { action: "complete" }), "Tour utilised against entitlement.")} className="rounded-lg border px-3 py-1 text-xs">
                                Step 09 — Record Utilisation
                              </button>
                            )}
                            {tour.status !== "cancelled" && tour.status !== "completed" && (
                              <button type="button" onClick={() => runAction(() => API.patch(`/admin/memberships/${selected._id}/tours/${tour._id}`, { action: "cancel" }), "Tour cancelled.")} className="rounded-lg border border-red-200 px-3 py-1 text-xs text-red-700">
                                Cancel
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        </section>
      )}
    </div>
  );
}
